import * as THREE from 'three';
import { wrapDelta } from '../sim/world';
import { meshOutline } from './outline';
import { PLAYER_MODEL_PATH, type ModelLibrary } from './models';
import { type AnimatedCharacter, type CharacterLibrary } from './characters';
import type { ItemDef } from '../content/types';
import { EquipmentRig } from './equipmentModels';

const MAX_PARTICLES = 3000;
const _m = new THREE.Matrix4();
const _p = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _s = new THREE.Vector3();
const _c = new THREE.Color();
const SPIN_AXIS = new THREE.Vector3(1, 1, 0).normalize();

/**
 * Pooled particle burst system (one InstancedMesh, cosmetic only — never
 * touches sim state). Used for hits, kills, dashes, pickups.
 */
export class Particles {
  readonly mesh: THREE.InstancedMesh;
  private x = new Float32Array(MAX_PARTICLES);
  private y = new Float32Array(MAX_PARTICLES);
  private z = new Float32Array(MAX_PARTICLES);
  private vx = new Float32Array(MAX_PARTICLES);
  private vy = new Float32Array(MAX_PARTICLES);
  private vz = new Float32Array(MAX_PARTICLES);
  private life = new Float32Array(MAX_PARTICLES);
  private maxLife = new Float32Array(MAX_PARTICLES);
  private size = new Float32Array(MAX_PARTICLES);
  private color = new Uint32Array(MAX_PARTICLES);
  private alive = new Uint8Array(MAX_PARTICLES);
  private cursor = 0;

  constructor(scene: THREE.Scene) {
    this.mesh = new THREE.InstancedMesh(new THREE.TetrahedronGeometry(0.5, 0), new THREE.MeshBasicMaterial({ color: 0xffffff, toneMapped: false }), MAX_PARTICLES);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
    this.mesh.setColorAt(0, _c.set(0xffffff));
    scene.add(this.mesh);
  }

  burst(x: number, z: number, count: number, color: number, speed = 6, life = 0.5, size = 0.18, up = 3): void {
    for (let k = 0; k < count; k++) {
      const i = this.cursor;
      this.cursor = (this.cursor + 1) % MAX_PARTICLES;
      const a = Math.random() * Math.PI * 2;
      const sp = speed * (0.4 + Math.random() * 0.8);
      this.x[i] = x;
      this.y[i] = 0.8;
      this.z[i] = z;
      this.vx[i] = Math.cos(a) * sp;
      this.vz[i] = Math.sin(a) * sp;
      this.vy[i] = up * Math.random();
      this.life[i] = life * (0.6 + Math.random() * 0.6);
      this.maxLife[i] = this.life[i];
      this.size[i] = size * (0.6 + Math.random() * 0.8);
      this.color[i] = color;
      this.alive[i] = 1;
    }
  }

  /** A short wake thrown opposite the player's dash direction. */
  dashWake(x: number, z: number, dx: number, dz: number, color = 0xdfffff): void {
    const px = -dz;
    const pz = dx;
    for (let k = 0; k < 16; k++) {
      const i = this.cursor;
      this.cursor = (this.cursor + 1) % MAX_PARTICLES;
      const lateral = (Math.random() - 0.5) * 5;
      const back = 5 + Math.random() * 7;
      this.x[i] = x + px * (Math.random() - 0.5) * 0.8;
      this.y[i] = 0.25 + Math.random() * 0.65;
      this.z[i] = z + pz * (Math.random() - 0.5) * 0.8;
      this.vx[i] = -dx * back + px * lateral;
      this.vz[i] = -dz * back + pz * lateral;
      this.vy[i] = 0.5 + Math.random() * 2;
      this.life[i] = 0.16 + Math.random() * 0.16;
      this.maxLife[i] = this.life[i];
      this.size[i] = 0.12 + Math.random() * 0.12;
      this.color[i] = color;
      this.alive[i] = 1;
    }
  }

  update(dt: number, camX = 0, camZ = 0, half = 1e9): void {
    let n = 0;
    for (let i = 0; i < MAX_PARTICLES; i++) {
      if (!this.alive[i]) continue;
      this.life[i] -= dt;
      if (this.life[i] <= 0) {
        this.alive[i] = 0;
        continue;
      }
      this.vy[i] -= 12 * dt;
      this.x[i] += this.vx[i] * dt;
      this.y[i] += this.vy[i] * dt;
      this.z[i] += this.vz[i] * dt;
      if (this.y[i] < 0.05) {
        this.y[i] = 0.05;
        this.vy[i] *= -0.3;
      }
      const f = this.life[i] / this.maxLife[i];
      _p.set(camX + wrapDelta(this.x[i] - camX, half), this.y[i], camZ + wrapDelta(this.z[i] - camZ, half));
      _q.setFromAxisAngle(SPIN_AXIS, i + this.life[i] * 6); // cheap spin
      _s.setScalar(this.size[i] * f);
      _m.compose(_p, _q, _s);
      this.mesh.setMatrixAt(n, _m);
      this.mesh.setColorAt(n, _c.set(this.color[i]));
      n++;
    }
    this.mesh.count = n;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }
}

/** Player avatar: animated KayKit rig (or capsule fallback) + fire-state ring. */
export class PlayerAvatar {
  readonly group = new THREE.Group();
  private body: THREE.Mesh | null = null;
  private character: AnimatedCharacter | null = null;
  private cannon: THREE.Mesh;
  private ring: THREE.Mesh;
  private ringMat: THREE.MeshBasicMaterial;
  private bodyMat: THREE.MeshToonMaterial | null = null;
  private equipment: EquipmentRig;

  constructor(scene: THREE.Scene, color: number, toon: THREE.MeshToonMaterial, models?: ModelLibrary, characters?: CharacterLibrary, characterId?: string, equipment?: readonly ItemDef[]) {
    this.character = characters?.create(characterId) ?? null;
    if (this.character) {
      toon.dispose();
      this.group.add(this.character.object);
    } else {
      this.bodyMat = toon;
      this.bodyMat.color.set(color);
      // Legacy single-model hook remains the fallback when KayKit assets fail.
      let bodyGeo: THREE.BufferGeometry = new THREE.CapsuleGeometry(0.4, 0.5, 3, 8);
      if (models?.has(PLAYER_MODEL_PATH)) {
        bodyGeo = models.get(PLAYER_MODEL_PATH)!;
        const visual = { geo: 'capsule' as const, color, gltf: PLAYER_MODEL_PATH };
        this.bodyMat.dispose();
        this.bodyMat = models.materialFor(visual);
      }
      this.body = new THREE.Mesh(bodyGeo, this.bodyMat);
      this.body.position.y = 0.75;
      this.body.castShadow = true;
      meshOutline(this.body, 0.05);
      this.group.add(this.body);
    }
    this.cannon = new THREE.Mesh(new THREE.ConeGeometry(0.22, 0.8, 6), new THREE.MeshBasicMaterial({ color: 0xbffbff, toneMapped: false }));
    this.cannon.rotation.x = Math.PI / 2;
    this.cannon.position.set(0, 0.85, 0.6);
    this.cannon.visible = !this.character;
    this.ringMat = new THREE.MeshBasicMaterial({ color: 0x7df9ff, transparent: true, opacity: 0.8, toneMapped: false });
    const rg = new THREE.RingGeometry(0.7, 0.85, 24);
    rg.rotateX(-Math.PI / 2);
    this.ring = new THREE.Mesh(rg, this.ringMat);
    this.ring.position.y = 0.06;
    this.equipment = new EquipmentRig(equipment, models);
    this.group.add(this.cannon, this.ring, this.equipment.group);
    scene.add(this.group);
  }

  update(x: number, z: number, facing: number, moving: boolean, dashing: boolean, firing: boolean, hitFlash: number, iframes: number, visible: boolean, t: number, dt: number): void {
    this.group.visible = visible;
    this.group.position.set(x, 0, z);
    this.group.rotation.y = -facing + Math.PI / 2;
    this.ringMat.color.set(dashing ? 0xffffff : firing ? 0x7df9ff : 0xff9a3c);
    this.ringMat.opacity = dashing ? 1 : firing ? 0.9 : 0.35 + 0.15 * Math.sin(t * 12);
    const flash = hitFlash > 0;
    this.bodyMat?.emissive.set(flash ? 0xffffff : 0x000000);
    const modelVisible = !(iframes > 0 && Math.floor(t * 30) % 2 === 0);
    if (this.body) this.body.visible = modelVisible;
    if (this.character) {
      this.character.object.visible = modelVisible;
      this.character.update(dt, dashing ? 'dash' : moving ? 'run' : 'idle', hitFlash);
    }
    const s = firing ? 1 + 0.05 * Math.sin(t * 20) : 1;
    this.body?.scale.set(s, 1 / s, s);
    this.equipment.update(t, firing, dashing);
  }

  dispose(scene: THREE.Scene): void {
    this.equipment.dispose();
    this.character?.dispose();
    this.bodyMat?.dispose();
    this.cannon.geometry.dispose();
    (this.cannon.material as THREE.Material).dispose();
    this.ring.geometry.dispose();
    this.ringMat.dispose();
    scene.remove(this.group);
  }
}
